<template>
  <div class="card my-3">
    <div class="card-header d-flex align-items-center">
      <div class="me-auto text-uppercase">
        <i class="fas fa-bars grabbable"></i>
        {{ component.type | translate }}
      </div>
      <div v-if="component.type !== 'video'">
        <div
          v-if="editing"
          class="btn-group"
          role="group"
          aria-label="Button group with nested dropdown"
        >
          <button @click.prevent="save" class="btn btn-primary">
            {{ 'Save' | translate }}
          </button>
          <button @click.prevent="cancel" class="btn btn-ourline-primary">
            {{ 'Cancel' | translate }}
          </button>
        </div>
        <div
          v-else
          class="btn-group"
          role="group"
          aria-label="Button group with nested dropdown"
        >
          <button class="btn btn-light" @click.prevent="edit">
            <i class="fas fa-pencil-alt"></i>
          </button>
          <div class="btn-group" role="group">
            <button
              :id="`btnGroupDrop${component.id}`"
              type="button"
              class="btn btn-light dropdown-toggle"
               data-bs-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            ></button>
            <div
              class="dropdown-menu"
              :aria-labelledby="`btnGroupDrop${component.id}`"
            >
              <a href="#" class="dropdown-item" @click.prevent="remove">
                {{ 'Delete' | translate }}
              </a>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="btn-group" role="group">
          <button
            :id="`btnGroupDrop${component.id}`"
            type="button"
            class="btn btn-light dropdown-toggle"
             data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          ></button>
          <div
            class="dropdown-menu"
            :aria-labelledby="`btnGroupDrop${component.id}`"
          >
            <a href="#" class="dropdown-item" @click.prevent="remove">
              {{ 'Delete' | translate }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <div class="card-body">
      <TextComponent
        v-if="component.type === 'text'"
        :component="component"
        :editing="editing"
        @quillChanged="quillChanged"
      ></TextComponent>

      <QuizComponent
        v-else-if="component.type === 'quiz'"
        :component="component"
        :editing="editing"
      ></QuizComponent>

      <AssignmentComponent
        v-else-if="component.type === 'assignment'"
        :component="component"
        :editing="editing"
        @fileDeleted="saveData"
        @fileUploaded="saveData"
      ></AssignmentComponent>

      <FileComponent
        v-else-if="_includes(['file', 'audio'], component.type)"
        :component="component"
        :editing="editing"
        @fileDeleted="deleteFile"
        @fileUploaded="saveFile"
      ></FileComponent>

      <HtmlComponent
        v-else-if="_includes(['html', 'code'], component.type)"
        :component="component"
        :editing="editing"
      ></HtmlComponent>

      <ImageComponent
        v-else-if="component.type === 'image'"
        :component="component"
        :editing="editing"
        @fileDeleted="deleteFile"
        @fileUploaded="saveFile"
      ></ImageComponent>

      <GalleryComponent
        v-else-if="component.type === 'gallery'"
        :component="component"
        :editing="editing"
        @fileDeleted="saveFileGallery"
        @fileUploaded="saveFileGallery"
      ></GalleryComponent>

      <VideoComponent
        v-else-if="component.type === 'video'"
        :component="component"
        :course="courseData"
        :section="sectionData"
        :lecture="lectureData"
        :editing="editing"
        @fileUploaded="saveVideo"
      ></VideoComponent>

      <VimeoComponent
        v-else-if="component.type === 'vimeo'"
        :component="component"
        :course="courseData"
        :section="sectionData"
        :lecture="lectureData"
        :editing="editing"
      ></VimeoComponent>

      <YouTubeComponent
        v-else-if="component.type === 'youtube'"
        :component="component"
        :course="courseData"
        :section="sectionData"
        :lecture="lectureData"
        :editing="editing"
      ></YouTubeComponent>
    </div>
  </div>
</template>

<script>
import route from './../../routes.js'
import TextComponent from './TextComponent'
import ImageComponent from './ImageComponent'
import GalleryComponent from './GalleryComponent'
import HtmlComponent from './HtmlComponent'
import VideoComponent from './VideoComponent'
import FileComponent from './FileComponent'
import QuizComponent from './QuizComponent'
import AssignmentComponent from './AssignmentComponent'
import VimeoComponent from './VimeoComponent'
import YouTubeComponent from './YouTubeComponent'

import _includes from 'lodash/includes'

export default {
  components: {
    TextComponent,
    ImageComponent,
    GalleryComponent,
    HtmlComponent,
    VideoComponent,
    FileComponent,
    QuizComponent,
    VimeoComponent,
    YouTubeComponent,
    AssignmentComponent,
  },
  props: ['componentData', 'sectionData', 'lectureData', 'courseData'],
  data() {
    return {
      editing: false,
      component: this.componentData,
    }
  },
  methods: {
    _includes,
    edit() {
      this.editing = true
    },
    handleError(error) {
      if (error.response && error.response.status === 419) {
        this.$root.$emit(
          'flash',
          this.$t('Your session has expired - please reload'),
          'warning',
        )
      } else {
        iziToast.error({
          title: 'Error',
          message: this.$t('Something went wrong'),
          position: 'bottomLeft',
        })
        console.error(error)
      }
    },
    saveFileGallery() {
      let component = this.component
      component.data.images = _.compact(component.data.images)
      axios
        .put(
          route.component(
            this.courseData,
            this.sectionData,
            this.lectureData,
            this.component,
          ).update,
          component,
        )
        .then((response) => {
          iziToast.success({
            title: 'OK',
            message: this.$t('Saved'),
            position: 'bottomLeft',
          })
        })
        .catch(this.handleError)
    },
    saveData() {
      let component = this.component
      axios
        .put(
          route.component(
            this.courseData,
            this.sectionData,
            this.lectureData,
            this.component,
          ).update,
          component,
        )
        .then((response) => {
          iziToast.success({
            title: 'OK',
            message: this.$t('Saved'),
            position: 'bottomLeft',
          })
        })
        .catch(this.handleError)
    },
    deleteFile() {
      axios
        .put(
          route.component(
            this.courseData,
            this.sectionData,
            this.lectureData,
            this.component,
          ).update,
          { file: null },
        )
        .then((response) => {
          iziToast.success({
            title: 'OK',
            message: this.$t('Deleted'),
            position: 'bottomLeft',
          })
        })
        .catch(this.handleError)
    },
    saveFile() {
      axios
        .put(
          route.component(
            this.courseData,
            this.sectionData,
            this.lectureData,
            this.component,
          ).update,
          this.component,
        )
        .then((response) => {
          iziToast.success({
            title: 'OK',
            message: this.$t('Saved'),
            position: 'bottomLeft',
          })
          this.editing = false
        })
        .catch(this.handleError)
    },
    saveVideo() {
      axios
        .put(
          route.component(
            this.courseData,
            this.sectionData,
            this.lectureData,
            this.component,
          ).update,
          {
            video: this.component.video,
            html: this.component.html,
          },
        )
        .then((response) => {
          iziToast.success({
            title: 'OK',
            message: this.$t('Saved'),
            position: 'bottomLeft',
          })
        })
        .catch(this.handleError)
    },
    save() {
      this.editing = false
      if (this.component.type === 'gallery') {
        return this.saveFileGallery()
      }
      axios
        .put(
          route.component(
            this.courseData,
            this.sectionData,
            this.lectureData,
            this.component,
          ).update,
          this.component,
        )
        .then((response) => {
          this.component = response.data
          iziToast.success({
            title: 'OK',
            message: this.$t('Updated'),
            position: 'bottomLeft',
          })
        })
        .catch(this.handleError)
    },
    cancel() {
      this.editing = false
    },
    remove() {
      axios
        .delete(
          route.component(
            this.courseData,
            this.sectionData,
            this.lectureData,
            this.component,
          ).delete,
        )
        .then((response) => {
          if (response) {
            this.$emit('remove', this.component)
          }
        })
        .then(this.displaySuccessMessage)
        .catch(this.handleError)
    },
    displaySuccessMessage() {
      iziToast.success({
        title: 'OK',
        message: this.$t('Saved'),
        position: 'bottomLeft',
      })
    },

    quillChanged(html, quill) {
      this.component.html = html
      this.component.quill = quill
    },
  },
}
</script>
